/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Inject, Injectable } from '@angular/core';
import { LoggedInUser } from './logged-in-user.model';
import { Observable, of } from 'rxjs';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { map, share, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AUTH_OPTIONS, AuthOptions, cookieTokenName, localStorageTokenName } from '../auth-options';
import { isEqual } from 'lodash';
import { LoggedInContextFromServerOptions, LoggedInContextProvider } from './logged-in-context.provider';
import { LOGGED_IN_CONTEXT_SERVICE } from './logged-in-context.config';
import { Cookies } from 'typescript-cookie';

@Injectable()
export class UserContext {
  private timeOfLastLoggedInCall: number | null = null;

  constructor(
    private readonly $localStorage: LocalStorageService,
    private readonly $sessionStorage: SessionStorageService,
    private readonly router: Router,
    @Inject(AUTH_OPTIONS) private readonly authOptions: AuthOptions,
    @Inject(LOGGED_IN_CONTEXT_SERVICE)
    private readonly loggedInUserProvider: LoggedInContextProvider
  ) {}

  hasAllRights(requiredRights: string[]): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    for (let i = 0; i < requiredRights.length; i++) {
      if (this.loggedInUserProvider.getLoggedInUser().authorities?.indexOf(requiredRights[i]) === -1) {
        return false;
      }
    }
    return true;
  }

  hasExactRights(rights: string[]): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    const authorities: Array<string> | undefined = this.loggedInUserProvider.getLoggedInUser().authorities;

    return authorities?.length == rights.length && this.hasAllRights(rights);
  }

  hasAnyRightsGroup(requiredRights: string[][] | string[]): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    for (let i = 0; i < requiredRights.length; i++) {
      const requiredRight = requiredRights[i];
      const requiredRightArray: string[] = Array.isArray(requiredRight) ? requiredRight : [requiredRight];
      if (this.hasAllRights(requiredRightArray)) {
        return true;
      }
    }
    return false;
  }

  hasAnyRights(requiredRights: string[]): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    for (let i = 0; i < requiredRights.length; i++) {
      if (this.loggedInUserProvider.getLoggedInUser().authorities?.indexOf(requiredRights[i]) !== -1) {
        return true;
      }
    }
    return false;
  }

  getAuthType(): string | undefined {
    if (!this.isLoggedIn()) {
      return undefined;
    }
    return this.loggedInUserProvider.getLoggedInUser().authType;
  }

  getCurrentUserEmail(): string | undefined {
    if (!this.isLoggedIn()) return undefined;

    const user: LoggedInUser = this.loggedInUserProvider.getLoggedInUser();

    if (user.authType === 'authEmail') {
      return user.name;
    }

    return user.email;
  }

  isEmailAuth(): Observable<boolean> {
    return this.getLoggedInUser().pipe(
      map((user) => user.authType),
      map((authType) => authType === 'authEmail'),
      share()
    );
  }

  /***
   * Check localStorage and sessionStorage for user jwt
   * @returns
   */
  isLoggedIn(): boolean {
    switch (this.authOptions.storage) {
      case 'local-storage': {
        return !!(
          this.$localStorage.retrieve(localStorageTokenName) || this.$sessionStorage.retrieve(localStorageTokenName)
        );
      }
      case 'cookie': {
        return !!Cookies?.get(cookieTokenName);
      }
    }
  }

  getLoggedInUser(): Observable<LoggedInUser> {
    return this.loggedInUserProvider.getLoggedInUserObservable();
  }

  getLoggedInUserFromServer(options?: LoggedInContextFromServerOptions): Observable<LoggedInUser> {
    return this.loggedInUserProvider.getLoggedInContextFromServer(options).pipe(
      tap((loggedInUser) => {
        this.updateCurrentUserStatus(loggedInUser);
      })
    );
  }

  getLoggedInUserFromServerIfNotPresent(debounceTime = 0) {
    return this.getLoggedInUser().pipe(
      switchMap((data) => {
        if (data && data.uuid) {
          return of(data);
        }
        return this.getLoggedInUserFromServerWithCaching(debounceTime);
      })
    );
  }

  getLoggedInUserFromServerWithCaching(debounceTime: number): Observable<LoggedInUser> {
    const currentTime = Date.now();

    if (this.timeOfLastLoggedInCall && currentTime - this.timeOfLastLoggedInCall < debounceTime) {
      return this.getLoggedInUser();
    }

    this.timeOfLastLoggedInCall = currentTime;
    return this.loggedInUserProvider.getLoggedInContextFromServer().pipe(
      tap((loggedInUser) => {
        this.updateCurrentUserStatus(loggedInUser);
      })
    );
  }

  getCurrentUser(): Observable<LoggedInUser> {
    return this.loggedInUserProvider.getCurrentUserStatus().asObservable();
  }

  updateCurrentUserStatus(currentUserStatus: LoggedInUser) {
    this.loggedInUserProvider
      .getCurrentUserStatus()
      .pipe(take(1))
      .subscribe((oldLoggedInUser) => {
        if (!isEqual(oldLoggedInUser, currentUserStatus)) {
          this.loggedInUserProvider.nextCurrentUserStatus(<LoggedInUser>currentUserStatus);
        }
      });
  }

  logout(redirectToLogin = true) {
    this.loggedInUserProvider.clearLoggedInUser();
    if (redirectToLogin) {
      this.redirectToLogin();
    }
    this.loggedInUserProvider.nextCurrentUserStatus(this.loggedInUserProvider.getLoggedInUser());
  }

  redirectToLogin() {
    this.router.navigate([`/auth/login`]);
  }
}
